import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
function formatPaymentDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatInvoiceDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatInvoiceSumAndFees(invoice) {
  const currency = String(invoice?.currency || "USDT_BSC");
  const paid = Number(invoice?.amountCrypto);
  const sumAmount =
    Number.isFinite(paid) && paid > 0
      ? Math.ceil(paid)
      : Number.isFinite(Number(invoice?.sum))
        ? Math.ceil(Number(invoice.sum))
        : null;
  return {
    sumLabel: sumAmount != null ? `${sumAmount} ${currency}` : invoice?.sumLabel || "—",
    feesLabel: `1 ${currency}`,
  };
}

function InvoiceDetailRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-3 last:border-0 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="break-all text-sm font-semibold text-navy-800 dark:text-gray-100 sm:max-w-[65%] sm:text-right">
        {children}
      </dd>
    </div>
  );
}

function BillingInvoiceModal({ invoice, loading, error, onClose }) {
  const invoiceAmounts = invoice ? formatInvoiceSumAndFees(invoice) : null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4 py-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-invoice-title"
      >
        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading invoice…
          </div>
        ) : error ? (
          <div className="p-6">
            <p className="text-sm font-semibold text-red-600 dark:text-red-400">{error}</p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                Close
              </button>
            </div>
          </div>
        ) : invoice ? (
          <>
            <div className="border-b border-gray-100 px-6 py-6 text-center dark:border-white/10">
              <p
                id="billing-invoice-title"
                className="text-3xl font-bold tracking-tight text-navy-900 dark:text-white"
              >
                {invoice.amountCryptoLabel}
              </p>
              {invoice.amountUsd ? (
                <p className="mt-1 text-lg font-semibold text-brand-500 dark:text-brand-300">
                  {invoice.amountUsd} USD
                </p>
              ) : null}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                  {invoice.currencyLabel}
                </span>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-800 dark:bg-blue-950/50 dark:text-blue-200">
                  ✓ {invoice.statusLabel}
                </span>
                {invoice.explorerUrl ? (
                  <a
                    href={invoice.explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-brand-600 hover:bg-gray-200 dark:bg-navy-900 dark:text-brand-300"
                  >
                    {invoice.explorerLabel} ↗
                  </a>
                ) : null}
              </div>
            </div>

            <dl className="px-6 py-2">
              {invoice.walletAddress ? (
                <InvoiceDetailRow label="To">{invoice.walletAddress}</InvoiceDetailRow>
              ) : null}
              <InvoiceDetailRow label="Type">{invoice.type}</InvoiceDetailRow>
              <InvoiceDetailRow label="Created at">
                {formatInvoiceDate(invoice.createdAt)}
              </InvoiceDetailRow>
              <InvoiceDetailRow label="ID">{invoice.paymentId}</InvoiceDetailRow>
              {invoice.confirmations != null ? (
                <InvoiceDetailRow label="Confirmations">
                  {invoice.confirmations}
                </InvoiceDetailRow>
              ) : null}
              <InvoiceDetailRow label="Sum">{invoiceAmounts.sumLabel}</InvoiceDetailRow>
              <InvoiceDetailRow label="Fees">{invoiceAmounts.feesLabel}</InvoiceDetailRow>
            </dl>

            <div className="flex justify-end border-t border-gray-100 px-6 py-4 dark:border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                Close
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function formatUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `$${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

function StatusBadge({ status }) {
  const s = String(status || "").toLowerCase();
  if (s === "confirmed") {
    return (
      <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
        Paid
      </span>
    );
  }
  if (s === "pending" || s === "awaiting_approval") {
    return (
      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
        Pending
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-800 dark:bg-red-950/50 dark:text-red-200">
        Failed
      </span>
    );
  }
  if (s === "cancelled" || s === "canceled") {
    return (
      <span className="inline-flex rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-bold text-gray-700 dark:bg-navy-900 dark:text-gray-300">
        Cancelled
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-700 dark:bg-navy-900 dark:text-gray-300">
      {s || "—"}
    </span>
  );
}

export default function Billing() {
  const [payments, setPayments] = useState([]);
  const [mock, setMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/billing-history"), {
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(
          clientSafeMessage(data.message, "Could not load payment history."),
        );
      }
      setPayments(Array.isArray(data.payments) ? data.payments : []);
      setMock(data.mock === true);
    } catch (e) {
      setError(clientSafeMessage(e?.message, "Could not load payment history."));
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  async function openInvoice(paymentId) {
    if (!paymentId) return;
    setInvoiceOpen(true);
    setInvoiceLoading(true);
    setInvoiceError("");
    setInvoiceData(null);
    try {
      const res = await fetch(
        apiUrl(
          `/api/client/billing-invoice?paymentId=${encodeURIComponent(paymentId)}`,
        ),
        { credentials: "include", cache: "no-store" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok || !data.invoice) {
        throw new Error(
          clientSafeMessage(data.message, "Could not load invoice."),
        );
      }
      setInvoiceData(data.invoice);
    } catch (e) {
      setInvoiceError(clientSafeMessage(e?.message, "Could not load invoice."));
    } finally {
      setInvoiceLoading(false);
    }
  }

  function closeInvoice() {
    setInvoiceOpen(false);
    setInvoiceData(null);
    setInvoiceError("");
  }

  const pendingCount = payments.filter(
    (p) => p.status === "pending" || p.status === "awaiting_approval",
  ).length;

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Billing & payments
            </h2>
            <p className="mt-2 max-w-xl text-sm text-gray-600 dark:text-gray-400">
              Slot subscriptions are paid in <strong>USDT (BEP20)</strong> via
              Plisio. Your payment history is listed below. To buy or renew a
              slot, start a new payment.
            </p>
            {mock ? (
              <p className="mt-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                Mock payment mode is active on this server (test data only).
              </p>
            ) : null}
          </div>
          <Link
            to="/PayUSDT"
            className="shrink-0 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            Pay for a slot
          </Link>
        </div>

        {pendingCount > 0 ? (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
            You have {pendingCount} pending payment
            {pendingCount > 1 ? "s" : ""}. Open{" "}
            <strong>Continue payment</strong> in the table or start a new payment
            above.
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="text-sm font-semibold text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}

      <div className="rounded-[20px] border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:p-6">
        <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Payment history
        </h3>
        <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-white/10">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-900/50">
                <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                  Date
                </th>
                <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                  Description
                </th>
                <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                  Amount
                </th>
                <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                  Status
                </th>
                <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                  >
                    Loading payments…
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                  >
                    No payments yet.{" "}
                    <Link
                      to="/PayUSDT"
                      className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
                    >
                      Pay for your first slot
                    </Link>
                    .
                  </td>
                </tr>
              ) : (
                payments.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-100 last:border-0 dark:border-white/5"
                  >
                    <td className="px-4 py-3 text-navy-800 dark:text-gray-200">
                      {formatPaymentDate(row.confirmedAt || row.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-gray-200">
                      <span className="font-semibold">{row.description}</span>
                      {row.subscriptionPath ? (
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {row.subscriptionPath}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-semibold tabular-nums text-navy-800 dark:text-gray-100">
                      {formatUsd(row.priceUsd)}
                      {row.payAmount != null ? (
                        <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-gray-400">
                          {Math.ceil(Number(row.payAmount))} USDT BEP20
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {row.status === "pending" ? (
                          <Link
                            to={`/PayUSDT?plan=${encodeURIComponent(row.planId || "")}`}
                            className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-600 dark:bg-brand-400"
                          >
                            Continue payment
                          </Link>
                        ) : null}
                        {row.canViewInvoice && row.paymentId ? (
                          <button
                            type="button"
                            onClick={() => void openInvoice(row.paymentId)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
                          >
                            Invoice
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
          Confirmed payments unlock your subscription slot. Manage accounts under{" "}
          <Link
            to="/client/subscription"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Subscription
          </Link>{" "}
          and risk on the{" "}
          <Link
            to="/client/dashboard"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Dashboard
          </Link>
          .
        </p>
      </div>

      {invoiceOpen ? (
        <BillingInvoiceModal
          invoice={invoiceData}
          loading={invoiceLoading}
          error={invoiceError}
          onClose={closeInvoice}
        />
      ) : null}
    </div>
  );
}
